import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText,
  CheckCircle,
  Clock,
  AlertTriangle,
  TrendingUp,
  MapPin,
  Activity,
} from 'lucide-react';
import { useQuery } from 'react-query';
import { useDashboardData } from '../hooks/useApi';
import StatCard from '../components/Dashboard/StatCard';
import ChartCard from '../components/Dashboard/ChartCard';
import MapCard from '../components/Dashboard/MapCard';
import ActivityCard from '../components/Dashboard/ActivityCard';
import NotificationCenterCard from '../components/Dashboard/NotificationCenterCard';
import PageHeader from '../components/Common/PageHeader';
import FormPageShell from '../components/Common/FormPageShell';
import DashboardSkeleton from '../components/Common/DashboardSkeleton';
import QueryErrorBanner from '../components/Common/QueryErrorBanner';
import { getAppHomePath } from '../constants/navigation';
import { usePermissions } from '../hooks/usePermissions';
import { useRouteAccess } from '../hooks/useRouteAccess';
import { licenseService, meService, processService } from '../services/api';
import { fetchAllPages } from '../utils/apiPagination';
import { P } from '../constants/permissions';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { TenantBannerBar } from '../components/Brand/TenantBrandMark';
import {
  DASHBOARD_WIDGETS,
  loadDashboardLayout,
  resolveDashboardProfile,
  saveDashboardLayout,
  visibleWidgets,
} from '../utils/dashboardLayout';
import {
  AnalystKpis,
  AnalystQueue,
  ComplaintKpis,
  ComplaintMix,
  ConsultationKpis,
  FieldProgress,
  InspectionKpis,
  InspectionQueue,
  LicenseCategoryBars,
  MonitoredEnterprises,
  OrgKpis,
  PendingDispatches,
  PortfolioKpis,
  ProfileBanner,
  RecentComplaints,
  TeamPerformance,
} from '../components/Dashboard/ProfileWidgets';

const PERIODS = [
  { value: 'week', labelKey: 'periodWeek' },
  { value: 'month', labelKey: 'periodMonth' },
  { value: '90d', labelKey: 'period90d' },
  { value: 'year', labelKey: 'periodYear' },
];

const PROFILE_LABELS = {
  gestor: 'profileGestor',
  analista: 'profileAnalista',
  consulta: 'profileConsulta',
  denuncias: 'profileDenuncias',
  fiscal: 'profileFiscal',
  coordenador: 'profileCoordenador',
  consultor: 'profileConsultor',
  requerente: 'profileRequerente',
};

function unwrap(payload) {
  return Array.isArray(payload) ? payload : payload?.data ?? payload?.items ?? [];
}

function RegularizacaoCard({ enabled }) {
  const { data, error } = useQuery(
    ['dash-plano'],
    () => licenseService.listPlanosRegularizacao().then((r) => r.data),
    { enabled }
  );
  const plans = Array.isArray(data) ? data : [];
  const latest = plans[0];
  if (!enabled) return null;
  if (error) return <QueryErrorBanner error={error} />;
  return (
    <Link to="/license/regularization-plans" className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 hover:border-emerald-400">
      <p className="text-xs uppercase text-emerald-800">Compliance / regularização</p>
      <p className="text-2xl font-semibold text-emerald-900">{latest ? `${latest.score}%` : '—'}</p>
      <p className="text-sm text-emerald-900">
        {latest ? `${(latest.items || []).length} ação(ões) no último plano` : 'Gere o plano em Relatórios → Score'}
      </p>
    </Link>
  );
}

function WorkQueues({ canInbox, canCond, canPlano }) {
  const { data: inboxRaw, error: inboxError } = useQuery(
    ['dash-inbox'],
    () => processService.getInbox().then((r) => r.data),
    { enabled: canInbox }
  );
  const { data: condRaw, error: condError } = useQuery(
    ['dash-cond-review'],
    () =>
      fetchAllPages((params) =>
        licenseService.searchCondicionantes({ ...params, situacao: '3' })
      ),
    { enabled: canCond }
  );
  const { data: myTasks, error: myTasksError } = useQuery(
    ['me-tasks'],
    () => meService.getTasks().then((r) => r.data)
  );
  const inbox = unwrap(inboxRaw);
  const conds = unwrap(condRaw);
  const summary = myTasks?.summary || {};
  return (
    <div className="space-y-3">
    {inboxError || condError || myTasksError ? (
      <QueryErrorBanner error={inboxError || condError || myTasksError} />
    ) : null}
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
      <Link to="/me" className="rounded-xl border border-primary-200 bg-primary-50 p-4 hover:border-primary-400">
        <p className="text-xs uppercase text-primary-800">Minhas tarefas</p>
        <p className="text-2xl font-semibold text-primary-900">{summary.total ?? 0}</p>
        <p className="text-sm text-primary-900">
          {summary.vencido || 0} vencida(s) · {summary.atencao || 0} em atenção
        </p>
      </Link>
      {canInbox ? (
        <Link to="/process/routing" className="rounded-xl border border-gray-200 bg-white p-4 hover:border-primary-300">
          <p className="text-xs uppercase text-gray-500">Tramitação</p>
          <p className="text-2xl font-semibold">{inbox.length}</p>
          <p className="text-sm text-primary-700">Abrir filas do órgão</p>
        </Link>
      ) : null}
      <RegularizacaoCard enabled={canPlano} />
      {canCond ? (
        <Link to="/license/conditions" className="rounded-xl border border-amber-200 bg-amber-50 p-4 hover:border-amber-400">
          <p className="text-xs uppercase text-amber-800">Condicionantes em atendimento</p>
          <p className="text-2xl font-semibold text-amber-900">{conds.length}</p>
          <p className="text-sm text-amber-900">Revisar evidências</p>
        </Link>
      ) : null}
    </div>
    </div>
  );
}

function CountList({ title, rows }) {
  const items = Array.isArray(rows) ? rows : [];
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <p className="text-xs uppercase text-gray-500">{title}</p>
      {items.length === 0 ? (
        <p className="mt-3 text-sm text-gray-500">Sem dados no período.</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {items.slice(0, 6).map((row) => (
            <li key={row.name || row.servidor} className="flex items-center justify-between text-sm">
              <span className="truncate pr-3 text-gray-700">{row.name || row.servidor}</span>
              <span className="font-semibold text-gray-900">{row.count ?? row.concluidas}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function InsightsPanel({ insights, periodLabel, t }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs uppercase text-gray-500">{t('leadTime')}</p>
          <p className="text-2xl font-semibold">{insights?.lead_time?.avg_days ?? 0}</p>
          <p className="text-sm text-gray-600">
            {t('leadTimeHint', { count: insights?.lead_time?.sample ?? 0 })}
          </p>
        </div>
        <CountList title={t('backlogOrgao')} rows={insights?.backlog_orgao} />
        <CountList title={t('backlogSetor')} rows={insights?.backlog_setor} />
        <CountList title={t('productivity')} rows={insights?.productivity} />
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ChartCard title={t('processFunnel')} data={insights?.process_funnel} type="bar" icon={TrendingUp} periodLabel={periodLabel} />
        <ChartCard title={t('complaintsByCategory')} data={insights?.complaints_by_category} type="bar" icon={AlertTriangle} periodLabel={periodLabel} />
        <ChartCard title={t('complaintsByBairro')} data={insights?.complaints_by_bairro} type="bar" icon={MapPin} periodLabel={periodLabel} />
        <ChartCard title={t('autosByStatus')} data={insights?.autos_by_status} type="pie" icon={FileText} periodLabel={periodLabel} />
      </div>
    </div>
  );
}

const Dashboard = () => {
  const { t } = useTranslation('dashboard');
  const { user } = useAuth();
  const { tenant } = useTenant();
  const [period, setPeriod] = useState('month');
  const [customize, setCustomize] = useState(false);
  const profile = useMemo(() => resolveDashboardProfile(user), [user]);
  const [layout, setLayout] = useState(() => loadDashboardLayout(user?.id, profile));

  useEffect(() => {
    setLayout(loadDashboardLayout(user?.id, profile));
  }, [user?.id, profile]);

  const persistLayout = (next) => {
    setLayout(next);
    saveDashboardLayout(user?.id, next);
  };

  const { stats, licensesByStatus, licensesTrend, insights, recentActivity, isLoading, isError, error } =
    useDashboardData(period);
  const { hasPermission } = usePermissions();
  const { canAccessRoute } = useRouteAccess();
  const canAccessMonitoring = canAccessRoute('/map');
  const canInbox = canAccessRoute('/process/routing');
  const canCond = hasPermission(P.LICENSE_LICENCAS);
  const canPlano = hasPermission(P.LICENSES_PAGE) || hasPermission(P.REPORTS_VIEW);
  const periodLabel = stats?.period_label || insights?.period_label || t('periodMonth');
  const shown = visibleWidgets(layout);

  const statCards = [
    {
      title: t('totalLicenses'),
      value: stats?.totalLicenses ?? 0,
      icon: FileText,
      color: 'primary',
      change: stats?.change,
      changeType: stats?.changeType,
      changeLabel: t('vsPrevious'),
    },
    {
      title: t('activeLicenses'),
      value: stats?.activeLicenses ?? 0,
      icon: CheckCircle,
      color: 'success',
    },
    {
      title: t('pending'),
      value: stats?.pendingLicenses ?? 0,
      icon: Clock,
      color: 'warning',
    },
    {
      title: t('expired'),
      value: stats?.expiredLicenses ?? 0,
      icon: AlertTriangle,
      color: 'danger',
    },
  ];

  const widgets = {
    profile: <ProfileBanner profile={profile} />,
    orgkpis: <OrgKpis insights={insights} />,
    consultationkpis: <ConsultationKpis stats={stats} />,
    analystkpis: <AnalystKpis insights={insights} />,
    complaintkpis: <ComplaintKpis />,
    inspectionkpis: <InspectionKpis />,
    portfoliokpis: <PortfolioKpis insights={insights} />,
    stats: (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </div>
    ),
    workqueues: <WorkQueues canInbox={canInbox} canCond={canCond} canPlano={canPlano} />,
    fieldprogress: <FieldProgress insights={insights} />,
    pendingdispatches: <PendingDispatches />,
    analystqueue: <AnalystQueue />,
    complaintmix: <ComplaintMix insights={insights} />,
    recentcomplaints: <RecentComplaints />,
    inspectionqueue: <InspectionQueue />,
    teamperformance: <TeamPerformance insights={insights} />,
    monitored: <MonitoredEnterprises />,
    insights: <InsightsPanel insights={insights} periodLabel={periodLabel} t={t} />,
    charts: (
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ChartCard title={t('byStatus')} data={licensesByStatus} type="pie" icon={TrendingUp} periodLabel={t('currentSnapshot')} />
        <ChartCard title={t('monthlyTrend')} data={licensesTrend} type="line" icon={Activity} periodLabel={periodLabel} />
        <LicenseCategoryBars licensesByStatus={licensesByStatus} />
      </div>
    ),
    activity: (
      <ActivityCard title={t('recentActivity')} data={recentActivity} icon={Activity} />
    ),
    notifications: canAccessMonitoring ? <NotificationCenterCard /> : null,
    map: canAccessMonitoring ? <MapCard title={t('enterprisesMap')} icon={MapPin} /> : null,
  };

  if (isLoading) {
    return (
      <FormPageShell>
        <DashboardSkeleton />
      </FormPageShell>
    );
  }

  return (
    <FormPageShell maxWidth="w-full max-w-none" className="!p-0">
      <div className="space-y-6">
        <PageHeader
          title={t('title')}
          description={t('description')}
          breadcrumbs={[{ label: t('common:home'), to: getAppHomePath() }, { label: t('title') }]}
        >
          <label className="text-sm text-gray-600">
            {t('period')}
            <select
              className="form-select ml-2 inline-block w-auto"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            >
              {PERIODS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="btn btn-outline btn-sm" onClick={() => setCustomize((v) => !v)}>
            {t('customize')}
          </button>
        </PageHeader>
        {isError ? <QueryErrorBanner error={error} /> : null}

        {customize ? (
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <p className="text-sm font-medium text-gray-800">
              {t('layoutFor', { profile: t(PROFILE_LABELS[profile]) })}
            </p>
            <ul className="mt-3 space-y-2">
              {(layout.order || []).map((id, index) => {
                const widget = DASHBOARD_WIDGETS.find((w) => w.id === id);
                if (!widget) return null;
                const hidden = (layout.hidden || []).includes(widget.id);
                return (
                  <li key={widget.id} className="flex items-center justify-between gap-3 text-sm">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!hidden}
                        onChange={(e) => {
                          const hiddenSet = new Set(layout.hidden || []);
                          if (e.target.checked) hiddenSet.delete(widget.id);
                          else hiddenSet.add(widget.id);
                          persistLayout({ ...layout, hidden: [...hiddenSet] });
                        }}
                      />
                      {widget.label}
                    </label>
                    <span className="flex gap-1">
                      <button
                        type="button"
                        className="btn btn-outline btn-xs"
                        disabled={index === 0}
                        onClick={() => {
                          const order = [...layout.order];
                          [order[index - 1], order[index]] = [order[index], order[index - 1]];
                          persistLayout({ ...layout, order });
                        }}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline btn-xs"
                        disabled={index === layout.order.length - 1}
                        onClick={() => {
                          const order = [...layout.order];
                          [order[index + 1], order[index]] = [order[index], order[index + 1]];
                          persistLayout({ ...layout, order });
                        }}
                      >
                        ↓
                      </button>
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}

        {shown.map((id) => {
          const node = widgets[id];
          if (!node) return null;
          return (
            <React.Fragment key={id}>
              <div>{node}</div>
              {id === 'map' ? (
                <div className="rounded-xl bg-white px-4 py-3">
                  <TenantBannerBar tenant={tenant} />
                </div>
              ) : null}
            </React.Fragment>
          );
        })}
        {!shown.includes('map') ? (
          <div className="rounded-xl bg-white px-4 py-3">
            <TenantBannerBar tenant={tenant} />
          </div>
        ) : null}
        <div className="h-10" aria-hidden />
      </div>
    </FormPageShell>
  );
};

export default Dashboard;
